import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncModelData, SyncOperationKind, OptimisticSyncUpdate } from "@/store/sync/types";
import { SessionKind, SessionModelData, SessionStatus } from "@/store/sessions/types";
import { uuidModule } from "@/modules/uuid";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ICreateSessionOperation } from "@/store/sync/operations/types";

export class CreateSessionOperation extends BaseSyncOperation<ICreateSessionOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_SESSION";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const sessionUpdate = this.generateCreateSessionUpdate();

    if (sessionUpdate) optimisticUpdates.push(sessionUpdate);

    return optimisticUpdates;
  }

  async triggerRecompute() {
    await this.store.sessions.recompute(this.payload.id);
  }

  protected generateCreateSessionUpdate(): OptimisticSyncUpdate<SessionModelData> {
    // Transform config to handle empty string variant which is not allowed in the model
    const transformedConfig = this.payload.config
      ? {
          ...this.payload.config,
          variant: this.payload.config.variant === "" ? null : this.payload.config.variant,
        }
      : this.payload.config;

    const optimisticUpdate: OptimisticSyncUpdate<SessionModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "SESSION",
        model_version: 1,
        model_data: {
          id: this.payload.id,
          owned_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          title: this.payload.title,
          status: this.payload.status as SessionStatus,
          kind: this.payload.kind as SessionKind,
          created_at: this.committedAt,
          config: transformedConfig,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return optimisticUpdate;
  }
}
